import type { RefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Chart as ChartJS } from 'chart.js';

import { useChartActions } from '@/hooks/useChartActions';

// Imported from the modules directly rather than the `../legend` barrel: the
// barrel also exports the `DatasetLegend` component, and pulling that in would
// drag a React component tree and its stylesheets into a pure-logic hook.
import { extractLegendItems } from '../legend/dataset-legend-helpers';
import type { LegendItem } from '../legend/types';
import { useChartTheme } from '../theme/ChartThemeContext';

import { buildMetricChartData } from './buildChartData';
import { buildMetricChartOptions } from './buildChartOptions';
import type { MetricChartData, MetricChartOptions } from './optionTypes';
import type { MetricSeriesSpec } from './seriesTypes';
import { themedSeries } from './themedSeries';
import type { MetricChartKind, MetricChartSpec } from './types';

export interface UseMetricChartResult<TKind extends MetricChartKind> {
    data: MetricChartData<TKind>;
    options: MetricChartOptions<TKind>;
    legendItems: LegendItem[];
    /** Indices into `spec.series` that are currently rendered. */
    visibleSeries: Set<number>;
    /** Takes an index into `spec.series`, as supplied by `LegendItem.datasetIndex`. */
    toggleSeries: (index: number) => void;
    chartRef: RefObject<ChartJS<TKind> | null>;
    isFullScreen: boolean;
    handleFullScreenToggle: () => void;
    fullScreenContainerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Resolves whether a series is currently hidden.
 *
 * Visibility is tracked by `series.key`, not by position. `toggledKeys` holds the
 * keys whose visibility the user has flipped away from the spec's declared
 * default, so effective hidden state is `hiddenByDefault XOR toggled`.
 *
 * That has three properties worth keeping:
 *  - reordering `spec.series` moves each series' state with it;
 *  - a series that did not exist before starts at its declared default rather
 *    than inheriting whatever the previous series at that index was set to;
 *  - a series that disappears and comes back remembers the user's choice.
 */
function isSeriesHidden(series: MetricSeriesSpec, toggledKeys: ReadonlySet<string>): boolean {
    return Boolean(series.hiddenByDefault) !== toggledKeys.has(series.key);
}

/**
 * Turns a `MetricChartSpec` into everything a chart card needs: Chart.js data and
 * options, legend state, and the imperative chart actions (PNG download, zoom
 * reset, fullscreen).
 *
 * Hidden series are dropped from the spec before the data is built, rather than
 * flagged `hidden` on the dataset. That keeps axis auto-scaling honest — the axis
 * fits what is actually on screen — and keeps dataset indices aligned with the
 * series list the tooltip looks up.
 */
export function useMetricChart<TKind extends MetricChartKind>(
    spec: MetricChartSpec<TKind>
): UseMetricChartResult<TKind> {
    const { chartRef, isFullScreen, handleFullScreenToggle, fullScreenContainerRef } =
        useChartActions<TKind>();

    const [toggledKeys, setToggledKeys] = useState<ReadonlySet<string>>(() => new Set<string>());
    const theme = useChartTheme();

    // A canvas legend is drawn and toggled by Chart.js itself.
    const legendItems = useMemo(
        () =>
            spec.legend === 'canvas'
                ? []
                : extractLegendItems(
                      spec.series.map((series, index) => ({
                          label: series.label,
                          legendConfig: {
                              show: series.showInLegend !== false,
                              color: themedSeries(series, theme).color,
                              order: index,
                              tooltipKey: series.tooltipKey,
                          },
                      }))
                  ),
        [spec.series, spec.legend, theme]
    );

    const visibleSeries = useMemo(() => {
        const visible = new Set<number>();

        spec.series.forEach((series, index) => {
            if (!isSeriesHidden(series, toggledKeys)) {
                visible.add(index);
            }
        });

        return visible;
    }, [spec.series, toggledKeys]);

    const toggleSeries = useCallback(
        (index: number) => {
            const target = spec.series[index];

            if (!target) {
                return;
            }

            // Refuse to hide the last series that still has a legend entry. The
            // legend component has its own version of this guard, but it counts all
            // visible series — so a chart carrying an always-on series that is kept
            // out of the legend (a trend line, a context metric) would otherwise let
            // the user hide every series they can actually see.
            const isHidingTarget = !isSeriesHidden(target, toggledKeys);
            if (isHidingTarget && target.showInLegend !== false) {
                const remainingInLegend = spec.series.filter(
                    (series) =>
                        series.key !== target.key &&
                        series.showInLegend !== false &&
                        !isSeriesHidden(series, toggledKeys)
                );

                if (remainingInLegend.length === 0) {
                    return;
                }
            }

            setToggledKeys((previous) => {
                const next = new Set(previous);

                if (next.has(target.key)) {
                    next.delete(target.key);
                } else {
                    next.add(target.key);
                }

                return next;
            });
        },
        [spec.series, toggledKeys]
    );

    const visibleSpec = useMemo(
        () => ({
            ...spec,
            series: spec.series.filter((series) => !isSeriesHidden(series, toggledKeys)),
        }),
        [spec, toggledKeys]
    );

    const data = useMemo(() => buildMetricChartData(visibleSpec, theme), [visibleSpec, theme]);
    const options = useMemo(
        () => buildMetricChartOptions(visibleSpec, theme),
        [visibleSpec, theme]
    );

    return {
        data,
        options,
        legendItems,
        visibleSeries,
        toggleSeries,
        chartRef,
        isFullScreen,
        handleFullScreenToggle,
        fullScreenContainerRef,
    };
}
